<template>
  <!-- An INTERACTIVE, published-looking mock of one portal "part" for the Layout
       Composer's Live Mobile preview. Behaves like the real site WITHOUT being the
       real app: header "My Account" dropdown opens, nav navigates (via @navigate),
       search returns sample parts you can add to a SHARED mock cart (`sim`), cart
       has qty steppers + place, pills pop, etc. -->
  <div class="cmp-part" :class="'cmp-k-' + partKey">
    <!-- HEADER -->
    <div v-if="partKey === 'header'" class="cmp-header">
      <img class="cmp-logo-img" :src="logoUrl" alt="JC Steele Service Portal" />
      <a class="cmp-phone" href="#" @click.prevent>(800) 278-3353</a>
      <div class="cmp-acct" @click.stop="acctOpen = !acctOpen">
        <span class="cmp-acct-dot"></span> My Account <span class="cmp-caret">▾</span>
        <ul v-if="acctOpen" class="cmp-acct-menu" @click.stop>
          <li @click="go('profile')">My Profile</li>
          <li @click="go('orderHistory')">Order History</li>
          <li @click="go('savedCarts')">Saved Carts</li>
          <li @click="go('documents')">Documents</li>
          <li @click="go('accountSetup')">Account Setup</li>
          <li class="cmp-acct-sep"></li>
          <li @click="go('signOut')">Sign Out</li>
        </ul>
      </div>
    </div>

    <!-- TOP NAV MENU -->
    <nav v-else-if="partKey === 'nav'" class="cmp-nav">
      <button class="cmp-burger" @click.stop="navOpen = !navOpen">☰</button>
      <ul class="cmp-nav-list" :class="{ open: navOpen }">
        <li v-for="n in navItems" :key="n" :class="{ active: navActive === n }"
            @click.stop="onNav(n)">{{ n }}</li>
      </ul>
    </nav>

    <!-- BREADCRUMB -->
    <div v-else-if="partKey === 'breadcrumb'" class="cmp-bc">
      <a href="#" @click.prevent.stop="$emit('navigate', 'home')">Home</a><span class="cmp-bc-sep">›</span><span>Cart</span>
    </div>

    <!-- HEADER SEARCH / SEARCH + BUILD CARD -->
    <div v-else-if="partKey === 'search'" class="cmp-search">
      <div class="cmp-search-row">
        <input v-model="searchText" placeholder="Search parts, machines…" @input="onSearch" @keyup.enter.stop="$emit('navigate', 'search')" />
        <button @click.stop="$emit('navigate', 'search')">Search</button>
      </div>
      <ul v-if="results.length" class="cmp-results">
        <li v-for="r in results" :key="r.sku" @click.stop="pick(r)">
          <b>{{ r.sku }}</b> — {{ r.name }} <span class="cmp-res-price">${{ r.price.toFixed(2) }}</span>
          <em class="cmp-res-add">+ Add</em>
        </li>
      </ul>
      <div v-else-if="searchText && !results.length" class="cmp-search-hint">No matches for “{{ searchText }}”.</div>
      <div v-if="added" class="cmp-added">Added {{ added }} to cart ✓</div>
    </div>

    <div v-else-if="partKey === 'buildcard'" class="cmp-card">
      <div class="cmp-card-title">Build your order</div>
      <composer-search-wizard :sim="sim" />
    </div>

    <!-- CONTENT CARD -->
    <div v-else-if="partKey === 'content'" class="cmp-card">
      <div class="cmp-card-title">Page content</div>
      <div class="cmp-line" v-for="n in 4" :key="n" :style="{ width: 55 + n * 9 + '%' }"></div>
    </div>

    <!-- CART (floating) -->
    <div v-else-if="partKey === 'cart'" class="cmp-cartw" :class="{ 'is-docked': cartDocked }">
      <template v-if="!cartDocked">
        <div class="cmp-cartw-head">
          <span class="cmp-cartw-title" @click.stop="cartOpen = !cartOpen">🛒 Your Cart ({{ count }})</span>
          <span class="cmp-cartw-tools">
            <button class="cmp-cartw-ico" @click.stop="cartOpen = !cartOpen" :title="cartOpen ? 'Collapse' : 'Expand'">{{ cartOpen ? '▾' : '▸' }}</button>
            <button class="cmp-cartw-ico" @click.stop="cartDocked = true" title="Dock to side">⇥</button>
          </span>
        </div>
        <div v-if="cartOpen" class="cmp-cartw-body">
          <composer-cart :sim="sim" @place="placed = true" />
        </div>
      </template>
      <button v-else class="cmp-cart-tab" @click.stop="cartDocked = false" title="Open cart">
        🛒 Cart ({{ count }}) ❮
      </button>
    </div>

    <!-- TOP PARTS (floating) -->
    <div v-else-if="partKey === 'topparts'" class="cmp-top" :class="{ 'is-docked': topDocked }">
      <template v-if="!topDocked">
        <div class="cmp-top-head">
          <span>⭐ Top 5 Parts</span>
          <button class="cmp-top-ico" @click.stop="topDocked = true" title="Dock to side">⇥</button>
        </div>
        <div v-for="p in topParts" :key="p.sku" class="cmp-top-row">
          <span>{{ p.sku }}</span>
          <button @click.stop="pick(p)">+</button>
        </div>
      </template>
      <button v-else class="cmp-top-tab" @click.stop="topDocked = false" title="Open Top 5">
        ⭐ Top 5 ❮
      </button>
    </div>

    <!-- FLOATING PILLS: Important Info / Guide / Account Setup -->
    <div v-else-if="partKey === 'info' || partKey === 'guide' || partKey === 'setup'" class="cmp-pillwrap">
      <button class="cmp-pill" @click.stop="onPill">{{ pillIcon }} {{ pillLabel }}</button>
      <div v-if="pillOpen" class="cmp-pop" @click.stop>
        <div class="cmp-pop-head">{{ pillLabel }}</div>
        <div class="cmp-pop-body">{{ pillBody }}</div>
        <button class="cmp-pop-close" @click.stop="pillOpen = false">Close</button>
      </div>
    </div>

    <!-- FOOTER -->
    <div v-else-if="partKey === 'footer'" class="cmp-footer">
      <a href="#" @click.prevent.stop="$emit('navigate', 'privacy')">Privacy</a> ·
      <a href="#" @click.prevent.stop="$emit('navigate', 'terms')">Terms</a> ·
      © JC Steele &amp; Sons · (800) 278-3353
    </div>

    <div v-else class="cmp-generic">{{ partKey }}</div>
  </div>
</template>

<script>
import { SAMPLE_PARTS, searchParts, addToCart, cartCount, cartTotal } from "@/assets/js/composerSampleParts";
import brushedLogo from "@/assets/img/jcsteeleservice-brushed-logo.png";
import ComposerSearchWizard from "./ComposerSearchWizard.vue";
import ComposerCart from "./ComposerCart.vue";

export default {
  name: "ComposerMockPart",
  components: { ComposerSearchWizard, ComposerCart },
  props: {
    partKey: { type: String, required: true },
    // Shared simulation state (the mock cart) passed from App.vue so search/cart
    // stay in sync across every part in the preview.
    sim: {
      type: Object,
      default: function () {
        return { cart: [] };
      },
    },
  },
  data() {
    return {
      acctOpen: false,
      navOpen: false,
      navActive: "Home",
      navItems: ["Home", "Order", "RFQ", "Documents", "History"],
      navMap: { Home: "home", Order: "order", RFQ: "rfq", Documents: "documents", History: "orderHistory" },
      searchText: "",
      results: [],
      added: "",
      cartOpen: true,
      cartDocked: false,
      topDocked: false,
      placed: false,
      pillOpen: false,
      logoUrl: brushedLogo,
      topParts: SAMPLE_PARTS.slice(0, 5),
    };
  },
  computed: {
    simCart() {
      return (this.sim && this.sim.cart) || [];
    },
    count() {
      return cartCount(this.sim);
    },
    total() {
      return cartTotal(this.sim);
    },
    pillIcon() {
      return { info: "ℹ️", guide: "🎬", setup: "⚙️" }[this.partKey] || "•";
    },
    pillLabel() {
      return { info: "Important Info", guide: "Guide", setup: "Account Setup" }[this.partKey] || this.partKey;
    },
    pillBody() {
      return {
        info: "Holiday shipping cutoff is Dec 18. Lead times on wear parts are running 2–3 weeks.",
        guide: "Watch a short walkthrough of this page. (Sample preview content.)",
        setup: "Master-only tools: copy setup, manage machines & links, event logs.",
      }[this.partKey] || "";
    },
  },
  methods: {
    go(page) {
      this.acctOpen = false;
      this.$emit("navigate", page);
    },
    onPill() {
      // The Account Setup pill opens the simulated setup page; Info/Guide pop a panel.
      if (this.partKey === "setup") this.$emit("navigate", "accountSetup");
      else this.pillOpen = !this.pillOpen;
    },
    onNav(n) {
      this.navActive = n;
      this.navOpen = false;
      this.$emit("navigate", this.navMap[n] || "home");
    },
    onSearch() {
      this.results = searchParts(this.searchText);
    },
    pick(part) {
      addToCart(this.sim, part);
      this.added = part.sku;
      this.results = [];
      this.searchText = "";
      this.placed = false;
      var self = this;
      setTimeout(function () {
        self.added = "";
      }, 1600);
    },
    dec(idx) {
      var c = this.simCart;
      if (c[idx]) c[idx].qty = Math.max(1, c[idx].qty - 1);
    },
  },
};
</script>

<style scoped>
.cmp-part {
  width: 100%;
  box-sizing: border-box;
  font-family: var(--cmp-font, -apple-system, "Segoe UI", Roboto, Arial, sans-serif);
  font-size: var(--cmp-fs, 13px);
  color: var(--cmp-color, #1f2733);
}
/* Header */
.cmp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 2px solid #c8302a;
  background: #fff;
}
.cmp-logo {
  font-weight: 800;
  color: #c8302a;
  letter-spacing: 0.04em;
}
.cmp-logo-img {
  height: 30px;
  width: auto;
  max-width: 60%;
  display: block;
}
.cmp-phone {
  color: var(--cmp-color, #555);
  text-decoration: none;
  font-size: 12px;
}
.cmp-acct {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #c8302a;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}
.cmp-acct-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #c8302a;
  display: inline-block;
}
.cmp-caret {
  font-size: 10px;
}
.cmp-acct-menu {
  position: absolute;
  top: 110%;
  right: 0;
  z-index: 20;
  min-width: 160px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #e0e3e7;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}
.cmp-acct-menu li {
  padding: 8px 14px;
  color: var(--cmp-color, #1f2733);
  font-weight: 500;
  white-space: nowrap;
}
.cmp-acct-menu li:hover {
  background: #fbe9e8;
  color: #c8302a;
}
.cmp-acct-sep {
  height: 1px;
  padding: 0 !important;
  margin: 4px 0;
  background: #eceff2;
}
/* Nav */
.cmp-nav {
  background: #2a2f36;
  color: #e6e8eb;
  padding: 6px 10px;
}
.cmp-burger {
  display: none;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
}
.cmp-nav-list {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.cmp-nav-list li {
  cursor: pointer;
  padding: 3px 2px;
  border-bottom: 2px solid transparent;
}
.cmp-nav-list li:hover {
  color: #fff;
}
.cmp-nav-list li.active {
  border-bottom-color: #c8302a;
  color: #fff;
  font-weight: 600;
}
/* Breadcrumb */
.cmp-bc {
  padding: 6px 10px;
  font-size: 12px;
  color: #777;
}
.cmp-bc a {
  color: #c8302a;
  text-decoration: none;
}
.cmp-bc-sep {
  margin: 0 6px;
  color: #bbb;
}
/* Search + build + cards */
.cmp-search,
.cmp-card {
  padding: 10px;
}
.cmp-card {
  border: 1px solid #e2e5e8;
  border-top: 3px solid #c8302a;
  border-radius: 8px;
  background: #fff;
  margin: 8px 10px;
}
.cmp-card-title {
  font-weight: 700;
  margin-bottom: 8px;
}
.cmp-search-row {
  display: flex;
  gap: 6px;
}
.cmp-search-row input {
  flex: 1 1 auto;
  min-width: 0;
  border: 1px solid #cfd3d7;
  border-radius: 5px;
  padding: 7px 9px;
  font-size: 13px;
}
.cmp-search-row button {
  border: 0;
  border-radius: 5px;
  background: #c8302a;
  color: #fff;
  font-weight: 600;
  padding: 7px 12px;
  cursor: pointer;
}
.cmp-results {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  border: 1px solid #e2e5e8;
  border-radius: 6px;
  overflow: hidden;
}
.cmp-results li {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border-bottom: 1px solid #f1f1f1;
  cursor: pointer;
  font-size: 12px;
}
.cmp-results li:hover {
  background: #fbe9e8;
}
.cmp-res-price {
  margin-left: auto;
  color: var(--cmp-color, #555);
}
.cmp-res-add {
  color: #c8302a;
  font-weight: 700;
  font-style: normal;
}
.cmp-search-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}
.cmp-added {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #2a7d2a;
}
.cmp-line {
  height: 8px;
  border-radius: 4px;
  background: #e9ecef;
  margin-bottom: 7px;
}
.cmp-build-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}
.cmp-build-list li {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #f1f1f1;
  font-size: 12px;
}
.cmp-x {
  color: #c8302a;
  font-weight: 700;
  cursor: pointer;
  margin-left: 6px;
}
/* Cart */
.cmp-cartw {
  margin: 8px 10px;
  border: 1px solid #c8302a;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.cmp-cartw-head {
  background: #c8302a;
  color: #fff;
  font-weight: 700;
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.cmp-cartw-body {
  padding: 8px 10px;
}
.cmp-cartw-title {
  cursor: pointer;
}
.cmp-cartw-tools {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.cmp-cartw-ico {
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  padding: 0;
}
/* Docked-to-side state: the cart collapses to a tab on the phone's right edge. */
.cmp-cartw.is-docked {
  margin: 0;
  border: 0;
  background: transparent;
  overflow: visible;
}
.cmp-cart-tab {
  position: absolute;
  right: 0;
  top: 130px;
  z-index: 30;
  border: 0;
  border-radius: 8px 0 0 8px;
  background: #13294b;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  padding: 12px 6px;
  writing-mode: vertical-rl;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
}
.cmp-cartw-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f1f1f1;
}
.cmp-qty {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cmp-qty button {
  width: 22px;
  height: 22px;
  border: 1px solid #cfd3d7;
  background: #f6f7f8;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
}
.cmp-qty b {
  display: inline-block;
  min-width: 18px;
  text-align: center;
}
.cmp-cartw-total {
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  margin-top: 8px;
}
.cmp-cartw-btn {
  width: 100%;
  margin-top: 8px;
  border: 0;
  border-radius: 6px;
  background: #c8302a;
  color: #fff;
  font-weight: 700;
  padding: 9px;
  cursor: pointer;
}
/* Top parts */
.cmp-top {
  margin: 8px 10px;
  border: 1px solid #e2e5e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.cmp-top-head {
  background: #2a2f36;
  color: #fff;
  font-weight: 700;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.cmp-top-ico {
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  padding: 0;
}
.cmp-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  border-bottom: 1px solid #f1f1f1;
}
.cmp-top-row button {
  width: 24px;
  height: 24px;
  border: 1px solid #c8302a;
  color: #c8302a;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-weight: 700;
}
/* Docked-to-side state (mirrors the cart): Top 5 collapses to a tab on the
   phone's right edge, offset below the cart tab so they don't overlap. */
.cmp-top.is-docked {
  margin: 0;
  border: 0;
  background: transparent;
  overflow: visible;
}
.cmp-top-tab {
  position: absolute;
  right: 0;
  top: 300px;
  z-index: 30;
  border: 0;
  border-radius: 8px 0 0 8px;
  background: #2a2f36;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  padding: 12px 6px;
  writing-mode: vertical-rl;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
}
/* Pills */
.cmp-pillwrap {
  position: relative;
  padding: 8px 10px;
}
.cmp-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: 18px;
  background: #c8302a;
  color: #fff;
  font-weight: 700;
  padding: 8px 16px;
  cursor: pointer;
}
.cmp-pop {
  position: absolute;
  z-index: 20;
  left: 10px;
  right: 10px;
  margin-top: 6px;
  background: #fff;
  border: 1px solid #e0e3e7;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  padding: 10px;
}
.cmp-pop-head {
  font-weight: 700;
  color: #c8302a;
  margin-bottom: 6px;
}
.cmp-pop-body {
  font-size: 12px;
  color: var(--cmp-color, #444);
  line-height: 1.4;
}
.cmp-pop-close {
  margin-top: 8px;
  border: 1px solid #cfd3d7;
  background: #f6f7f8;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
}
/* Footer */
.cmp-footer {
  background: #2a2f36;
  color: #cfd3d7;
  text-align: center;
  font-size: 11px;
  padding: 12px 10px;
}
.cmp-footer a {
  color: #e6e8eb;
  text-decoration: none;
}
.cmp-generic {
  padding: 10px;
  color: #888;
}
@media (max-width: 480px) {
  .cmp-burger {
    display: block;
  }
  .cmp-nav-list {
    display: none;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-top: 6px;
  }
  .cmp-nav-list.open {
    display: flex;
  }
}
</style>
